import { AxiosInstance, AxiosError } from 'axios';
import { AuthenticationError } from '../../../domain/errors/AuthenticationError';
import { NetworkError } from '../../../domain/errors/NetworkError';
import { ValidationError } from '../../../domain/errors/ValidationError';

/**
 * ErrorInterceptor
 * Transforms HTTP errors into domain-specific errors
 */
export class ErrorInterceptor {
  constructor(private axiosInstance: AxiosInstance) {}

  /**
   * Register the interceptor
   */
  public register(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const transformedError = this.transformError(error);
        return Promise.reject(transformedError);
      },
    );
  }

  /**
   * Transform Axios errors into domain errors
   */
  private transformError(error: AxiosError): Error {
    const statusCode = error.response?.status;
    const responseData = error.response?.data as {
      message?: string;
      errors?: Record<string, string[]>;
    };

    // Authentication errors (401)
    if (statusCode === 401) {
      return AuthenticationError.unauthorized();
    }

    // Forbidden errors (403)
    if (statusCode === 403) {
      return new AuthenticationError('Forbidden access', 'FORBIDDEN');
    }

    // Validation errors (422)
    if (statusCode === 422 && responseData?.errors) {
      return ValidationError.multipleErrors(responseData.errors);
    }

    // Bad request (400)
    if (statusCode === 400) {
      return new ValidationError(responseData?.message || 'Bad request', undefined);
    }

    // Network timeout
    if (error.code === 'ECONNABORTED') {
      return NetworkError.timeout();
    }

    // No response (network unavailable)
    if (!error.response) {
      return NetworkError.networkUnavailable();
    }

    // Generic server error
    return NetworkError.serverError(statusCode || 500, responseData?.message || error.message);
  }
}
